import React, { useState, useEffect, useRef } from 'react';
import {
  PlusIcon,
  VideoCameraIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  ClockIcon,
  UsersIcon,
  PlayIcon,
  StopIcon,
  EyeIcon,
  EyeSlashIcon,
  AcademicCapIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BookOpenIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import PageHeader from '../components/PageHeader';
import { useToast } from '../components/ToastProvider';
import '../styles/forms.css';

const LiveSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  
  // Course dropdown states
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const courseDropdownRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 120,
    privacy_status: 'private',
    batches: [],
    meeting_link: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Handle clicking outside course dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(event.target)) {
        setShowCourseDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { notify } = useToast();

  const fetchData = async () => {
    try {
      const [sessionsRes, coursesRes, batchesRes] = await Promise.allSettled([
        apiService.getLiveSessions(),
        apiService.getMyCourses(),
        apiService.getBatches(),
      ]);

      if (sessionsRes.status === 'fulfilled') {
        const value = sessionsRes.value;
        const normalized = Array.isArray(value)
          ? value
          : Array.isArray(value?.results)
          ? value.results
          : Array.isArray(value?.data)
          ? value.data
          : [];
        setSessions(normalized);
        console.log('Live sessions loaded:', normalized.length);
      } else {
        console.warn('Live sessions fetch failed:', sessionsRes.reason?.message || sessionsRes.reason);
        setSessions([]);
      }

      if (coursesRes.status === 'fulfilled') {
        const value = coursesRes.value;
        const normalized = Array.isArray(value)
          ? value
          : Array.isArray(value?.results)
          ? value.results
          : Array.isArray(value?.data)
          ? value.data
          : [];
        setCourses(normalized);
      } else {
        console.warn('Courses fetch failed:', coursesRes.reason?.message || coursesRes.reason);
        setCourses([]);
      }

      if (batchesRes.status === 'fulfilled') {
        setBatches(Array.isArray(batchesRes.value) ? batchesRes.value : []);
      } else {
        console.warn('Batches fetch failed:', batchesRes.reason?.message || batchesRes.reason);
        setBatches([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      notify('Some data failed to load. Retrying might help.', { type: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate required fields
      if (!formData.title.trim()) {
        notify('Session title is required', { type: 'error' });
        return;
      }
      if (!formData.course) {
        notify('Please select a course', { type: 'error' });
        return;
      }
      if (!formData.scheduled_date || !formData.scheduled_time) {
        notify('Please select both date and time for the session', { type: 'error' });
        return;
      }

      // Debug: Log the form data before processing
      console.log('Form data before processing:', formData);
      console.log('Scheduled date:', formData.scheduled_date);
      console.log('Scheduled time:', formData.scheduled_time);

      // Create the scheduled time object for validation and processing
      const scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time}:00`);
      
      // Validate that the scheduled time is in the future
      if (scheduledDateTime <= new Date()) {
        notify('Scheduled time must be in the future', { type: 'error' });
        return;
      }

      // Convert to UTC ISO string to avoid timezone issues
      const scheduledTime = scheduledDateTime.toISOString();
      
      const sessionData = {
        title: formData.title,
        description: formData.description,
        course: formData.course,
        scheduled_time: scheduledTime,
        duration_minutes: parseInt(formData.duration_minutes),
        privacy_status: formData.privacy_status,
        batches: formData.batches,
        meeting_link: formData.meeting_link,
        notes: formData.notes,
      };

      // Debug: Log the data being sent
      console.log('Sending session data:', sessionData);
      console.log('Scheduled time:', scheduledTime);

      if (editingSession) {
        await apiService.updateLiveSession(editingSession.id, sessionData);
        notify('Session updated successfully', { type: 'success' });
      } else {
        await apiService.createLiveSession(sessionData);
        notify('Session created successfully', { type: 'success' });
      }
      
      setShowCreateModal(false);
      setEditingSession(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save session:', error);
      notify('Failed to save session', { type: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 120,
      privacy_status: 'private',
      batches: [],
      meeting_link: '',
      notes: '',
    });
    setSelectedCourse(null);
    setCourseSearchTerm('');
    setShowCourseDropdown(false);
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setShowCourseDropdown(false);
    setCourseSearchTerm('');
    setFormData(prev => ({ ...prev, course: course.id }));
  };

  // Filter courses based on search term
  const filteredCourses = courses.filter(course =>
    (course.name || course.title || '').toLowerCase().includes(courseSearchTerm.toLowerCase())
  );

  const handleEdit = (session) => {
    const scheduledTime = new Date(session.scheduled_time);
    const date = scheduledTime.toISOString().split('T')[0];
    const time = scheduledTime.toTimeString().slice(0, 5);
    
    setEditingSession(session);
    setFormData({
      title: session.title,
      description: session.description,
      course: session.course,
      scheduled_date: date,
      scheduled_time: time,
      duration_minutes: session.duration_minutes || 120,
      privacy_status: session.privacy_status || 'private',
      batches: session.batches || [],
      meeting_link: session.meeting_link || '',
      notes: session.notes || '',
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (sessionId) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      try {
        await apiService.deleteLiveSession(sessionId);
        notify('Session deleted successfully', { type: 'success' });
        fetchData();
      } catch (error) {
        console.error('Failed to delete session:', error);
        notify('Failed to delete session', { type: 'error' });
      }
    }
  };

  const handleStartSession = async (session) => {
    try {
      await apiService.startLiveSession(session.id);
      notify('Live session started successfully!', { type: 'success' });
      fetchData();
    } catch (error) {
      console.error('Failed to start session:', error);
      notify('Failed to start session', { type: 'error' });
    }
  };

  const handleEndSession = async (session) => {
    try {
      await apiService.endLiveSession(session.id);
      notify('Live session ended successfully!', { type: 'success' });
      fetchData();
    } catch (error) {
      console.error('Failed to end session:', error);
      notify('Failed to end session', { type: 'error' });
    }
  };

  const handleCancelSession = async (session) => {
    if (window.confirm('Are you sure you want to cancel this session?')) {
      try {
        await apiService.cancelLiveSession(session.id);
        notify('Session cancelled successfully', { type: 'success' });
        fetchData();
      } catch (error) {
        console.error('Failed to cancel session:', error);
        notify('Failed to cancel session', { type: 'error' });
      }
    }
  };

  const handleCreateYouTubeStream = async (session) => {
    try {
      setLoading(true);
      const response = await apiService.createYouTubeStream(session.id);
      notify('YouTube stream created successfully!', { type: 'success' });
      
      // Show stream details if available
      if (response.stream_data) {
        console.log('YouTube Stream Details:', response.stream_data);
        notify(`Stream URL: ${response.stream_data.stream_url}`, { type: 'info', duration: 10000 });
      }
      
      fetchData();
    } catch (error) {
      console.error('Failed to create YouTube stream:', error);
      
      // Extract error message from response
      let errorMessage = 'Failed to create YouTube stream';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Show specific error messages
      if (errorMessage.includes('credentials not configured')) {
        notify('YouTube API is not properly configured. Please contact your administrator.', { type: 'error', duration: 8000 });
      } else if (errorMessage.includes('quota exceeded')) {
        notify('YouTube API quota exceeded. Please try again later.', { type: 'error', duration: 6000 });
      } else if (errorMessage.includes('permissions insufficient')) {
        notify('YouTube API permissions are insufficient. Please check your Google account.', { type: 'error', duration: 6000 });
      } else {
        notify(errorMessage, { type: 'error', duration: 6000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'testing': return 'bg-yellow-100 text-yellow-800';
      case 'live': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'private': return <EyeSlashIcon className="h-4 w-4" />;
      case 'unlisted': return <EyeIcon className="h-4 w-4" />;
      case 'public': return <EyeIcon className="h-4 w-4" />;
      default: return <EyeSlashIcon className="h-4 w-4" />;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked,
      }));
    } else if (name === 'batches') {
      // Handle multiple batch selection
      const selectedBatches = Array.from(e.target.selectedOptions, option => option.value);
      setFormData(prev => ({
        ...prev,
        batches: selectedBatches,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const getCourseBatches = (courseId) => {
    if (!courseId) return [];
    return batches.filter(batch => batch.course === courseId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Live Sessions"
        subtitle="Schedule and manage live online classes"
        actions={(
          <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500">
            <PlusIcon className="h-4 w-4 mr-2" />
            Schedule Session
          </button>
        )}
      />

      {/* Sessions Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {sessions.map((session) => (
          <div key={session.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <VideoCameraIcon className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">{session.title}</h3>
                    <p className="text-sm text-gray-500">{session.course_name || 'Course'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {session.status === 'live' && (
                    <div className="flex items-center text-green-600">
                      <div className="w-2 h-2 bg-green-600 rounded-full mr-1 animate-pulse"></div>
                      <span className="text-xs">LIVE</span>
                    </div>
                  )}
                  <button
                    onClick={() => handleEdit(session)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(session.id)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {session.description}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-500">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {new Date(session.scheduled_time).toLocaleDateString()}
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  {new Date(session.scheduled_time).toLocaleTimeString()} ({session.duration_minutes} min)
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <UsersIcon className="h-4 w-4 mr-2" />
                  {session.total_students_eligible || 0} eligible students
                </div>
                {session.batches && session.batches.length > 0 && (
                  <div className="flex items-center text-sm text-gray-500">
                    <AcademicCapIcon className="h-4 w-4 mr-2" />
                    {session.batches.length} batch(es) selected
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-500">
                  {getPrivacyIcon(session.privacy_status)}
                  <span className="ml-2 capitalize">{session.privacy_status}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                  {session.status}
                </span>
                <div className="flex space-x-2">
                  {/* YouTube Stream Actions */}
                  {!session.youtube_broadcast_id && session.status === 'draft' && (
                    <button 
                      onClick={() => handleCreateYouTubeStream(session)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                    >
                      <VideoCameraIcon className="h-3 w-3 mr-1" />
                      Create Stream
                    </button>
                  )}
                  
                  {/* Session Control Actions */}
                  {session.can_start && (
                    <button 
                      onClick={() => handleStartSession(session)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    >
                      <PlayIcon className="h-3 w-3 mr-1" />
                      Start
                    </button>
                  )}
                  
                  {session.can_end && (
                    <button 
                      onClick={() => handleEndSession(session)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                    >
                      <StopIcon className="h-3 w-3 mr-1" />
                      End
                    </button>
                  )}
                  
                  {session.status === 'draft' && (
                    <button 
                      onClick={() => handleCancelSession(session)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700"
                    >
                      <TrashIcon className="h-3 w-3 mr-1" />
                      Cancel
                    </button>
                  )}
                  
                  {/* Join Link for Live Sessions */}
                  {session.status === 'live' && session.student_join_url && (
                    <a 
                      href={session.student_join_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <VideoCameraIcon className="h-3 w-3 mr-1" />
                      Join
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sessions.length === 0 && (
        <div className="text-center py-12">
          <VideoCameraIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-semibold text-gray-900">No live sessions scheduled</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by scheduling your first live session to engage with your students.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Schedule Session
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowCreateModal(false)}>
          <div className="relative top-8 mx-auto p-6 border w-full max-w-4xl shadow-2xl rounded-xl bg-white" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              aria-label="Close"
              className="absolute top-3 right-3 p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              onClick={() => setShowCreateModal(false)}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {editingSession ? 'Edit Live Session' : 'Schedule New Live Session'}
              </h3>
              <p className="text-sm text-gray-600">
                Create engaging live sessions to connect with your students in real-time
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Session Details Section */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                  <VideoCameraIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Session Details
                </h4>
                <div className="space-y-4">
                <div>
                    <label className="form-label form-label-required">Session Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                      className="input-field"
                      placeholder="Enter session title..."
                  />
                </div>
                
                <div>
                    <label className="form-label form-label-required">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={3}
                      className="input-field"
                      placeholder="Describe what this session will cover..."
                  />
                  </div>
                </div>
                </div>

              {/* Course Selection Section */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                  <AcademicCapIcon className="h-5 w-5 mr-2 text-green-600" />
                  Course Selection
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="form-label form-label-required">Select Course</label>
                    <div className="relative z-10" ref={courseDropdownRef}>
                      <div
                        className={`input-field cursor-pointer flex items-center justify-between ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => !loading && setShowCourseDropdown(!showCourseDropdown)}
                      >
                        <div className="flex-1">
                          {selectedCourse ? (
                            <div className="flex items-center justify-between">
                <div>
                                <span className="font-medium text-gray-900">{selectedCourse.name || selectedCourse.title}</span>
                                {selectedCourse.duration_months && (
                                  <span className="text-sm text-gray-500 ml-2">({selectedCourse.duration_months} months)</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">{(courses || []).length === 0 ? 'No courses available' : 'Search and select a course...'}</span>
                          )}
                        </div>
                        {!loading && (
                          <div className="ml-2 flex-shrink-0">
                            {showCourseDropdown ? (
                              <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        )}
                      </div>

                      {showCourseDropdown && !loading && (
                        <div className="absolute left-0 right-0 z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-2xl ring-1 ring-black/5 max-h-80 overflow-hidden pl-3 pb-3">
                          <div className="p-3 border-b border-gray-100">
                            <div className="relative">
                              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search courses..."
                                value={courseSearchTerm}
                                onChange={(e) => setCourseSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />
                            </div>
                          </div>

                          <div className="max-h-60 overflow-y-auto">
                            {filteredCourses.length === 0 ? (
                              <div className="p-4 text-center text-gray-500">No courses found</div>
                            ) : (
                              filteredCourses.map((course) => (
                                <div
                                  key={course.id}
                                  className="py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                  onClick={() => handleCourseSelect(course)}
                                >
                                  <div className="flex items-start justify-between px-0 pr-4">
                                    <div className="text-left">
                                      <div className="font-medium text-gray-900">{course.name || course.title}</div>
                                      {course.duration_months && (
                                        <div className="text-sm text-gray-500">Duration: {course.duration_months} months</div>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-2 text-xs text-gray-400">
                                      <ClockIcon className="h-3 w-3" />
                                      <span>{course.duration_months || 'N/A'} months</span>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                </div>

              {/* Schedule Configuration Section */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-purple-600" />
                  Schedule Configuration
                </h4>
                <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="form-label form-label-required">Date</label>
                    <input
                      type="date"
                      name="scheduled_date"
                      value={formData.scheduled_date}
                      onChange={handleChange}
                      required
                        className="input-field"
                    />
                  </div>
                  <div>
                      <label className="form-label form-label-required">Time</label>
                    <input
                      type="time"
                      name="scheduled_time"
                      value={formData.scheduled_time}
                      onChange={handleChange}
                      required
                        className="input-field"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="form-label form-label-required">Duration (minutes)</label>
                    <input
                      type="number"
                      name="duration_minutes"
                      value={formData.duration_minutes}
                      onChange={handleChange}
                      required
                      min="15"
                        max="480"
                        className="input-field"
                        placeholder="120"
                    />
                  </div>
                  <div>
                      <label className="form-label form-label-required">Privacy Status</label>
                    <select
                      name="privacy_status"
                      value={formData.privacy_status}
                      onChange={handleChange}
                      required
                        className="input-field"
                      >
                        <option value="private">🔒 Private</option>
                        <option value="unlisted">🔗 Unlisted</option>
                        <option value="public">🌐 Public</option>
                    </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Settings Section */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                  <BookOpenIcon className="h-5 w-5 mr-2 text-orange-600" />
                  Additional Settings
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="form-label">Meeting Link (Optional)</label>
                    <input
                      type="url"
                      name="meeting_link"
                      value={formData.meeting_link}
                      onChange={handleChange}
                      placeholder="https://meet.google.com/..."
                      className="input-field"
                    />
                    <p className="form-help-text">Alternative meeting link if not using YouTube Live</p>
                  </div>

                  <div>
                    <label className="form-label">Session Notes (Optional)</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Additional notes, agenda, or instructions for this session..."
                      className="input-field"
                    />
                    <p className="form-help-text">Internal notes for session preparation</p>
                </div>

                <div>
                    <label className="form-label">Select Batches</label>
                  <select
                    name="batches"
                    multiple
                    value={formData.batches}
                    onChange={handleChange}
                      className="input-field"
                    size="3"
                  >
                    {getCourseBatches(formData.course).map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {batch.name} ({batch.status})
                      </option>
                    ))}
                  </select>
                    <p className="form-help-text">Hold Ctrl/Cmd to select multiple batches. Only students in selected batches can join.</p>
                </div>
                </div>
                </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingSession(null);
                      resetForm();
                    }}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 border border-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                  disabled={loading}
                  className="px-6 py-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {editingSession ? 'Update Session' : 'Schedule Session'}
                    </>
                  )}
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveSessions; 